import { AdvanceTable, BasicTable, DangerAlert, IBasicTableColumn, IconTextButton, Modal, } from 'ui-lib';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
    DeviceDeploymentsStore,
    IDisplayDeploymentProps,
} from '../../../../../store/device-details/device-deployments-store';
import { IDeviceTableProps } from './components';
import { useDeviceStore, useSnapshotStore } from 'store';
import devicesCss from './devices.css';
import { DEFAULT_HISTORICAL_RANGE } from '../../../../../common-utils/date-utils';
import { displaySnapshotDetailsModal, useReleaseDetails } from 'pages/private/components/release-info-component';
import { ConstrainedDateRangePicker } from 'pages/private/components/constrained-date-range-picker';

export const DeviceDeploymentsTable = observer((props: IDeviceTableProps) => {
    const devicesStore = useDeviceStore();
    const snapshotStore = useSnapshotStore();
    const memoizedStore = React.useMemo(() => {
        const store = new DeviceDeploymentsStore({
            accountId: props.accountId,
            uid: props.uid,
            devicesStore: devicesStore,
            serialNumber: props.serialNumber,
        });
        snapshotStore.loadEntities(props.accountId);
        store.initialize();
        return store;
    }, []);


    const loading = memoizedStore.loading || snapshotStore.dataLoading;

    const [modalVisible, setModalVisible] = React.useState<boolean>(false);
    const [modalSource, setModalSource] = React.useState<IDisplayDeploymentProps>();

    const [releaseModalVisible, setReleaseModalVisible] = React.useState<boolean>(false);
    const handleCancel = () => {
        setModalVisible(false);
    };

    const collectData = (record: IDisplayDeploymentProps) => {
        setModalVisible(true);
        setModalSource(record);
    };

    const releaseStates = useReleaseDetails({
        snapshotStore,
    });

    React.useEffect(() => {
        if (!releaseModalVisible) {
            return;
        }
        displaySnapshotDetailsModal({
            release: releaseStates,
            onClose: () => {
                setReleaseModalVisible(false);
            },
        });
    }, [releaseModalVisible]);

    const collectPackageModalData = (snapshotId: number) => {
        releaseStates.updateReleaseId(snapshotId);
        setReleaseModalVisible(true);
    };

    const columns: IBasicTableColumn[] = [
        {
            code: 'jobName',
            title: 'Deployment Name',
            dataIndex: 'jobName',
            actions: [
                {
                    key: 'key',
                    onClick: (key: string) =>
                        collectData(memoizedStore.displayDeployments.find((item) => item.key == key)),
                },
            ],
        },
        {
            code: 'snapshots',
            title: 'Releases',
            dataIndex: 'snapshots',
            render: (snapshots: any[], record: any) => {
                if (snapshots?.length === 0) return null;
                const separator = <p className={devicesCss.separator}>, </p>;
                return (
                    <div className={devicesCss.snapshots}>
                        {snapshots
                            ?.map((snapshot) => (
                                <IconTextButton
                                    className={devicesCss.snapshotNameButton}
                                    type='link'
                                    label={snapshot.label}
                                    onClick={() => collectPackageModalData(snapshot.id)}
                                    key={snapshot.id}
                                />
                            ))
                            .reduce(
                                (prev, current, index) =>
                                    index === 0 ? prev.concat(current) : prev.concat(separator, current),
                                [],
                            )}
                    </div>
                );
            },
        },
        {
            code: 'jobId',
            title: 'Job Id',
            dataIndex: 'jobId',
            render: (jobId: string) => <span className={devicesCss.jobId}>{jobId}</span>,
        },
        { code: 'status', title: 'Status', dataIndex: 'status' },
        { code: 'displayTimestamp', title: 'Created', dataIndex: 'displayTimestamp', width: '200px' },
    ];

    const LogModal = () => {
        const columns: IBasicTableColumn[] = [
            { code: 'name', title: 'Name', dataIndex: 'name' },
            { code: 'error', title: 'Error', dataIndex: 'error' },
            { code: 'notice', title: 'Notice', dataIndex: 'notice' },
            { code: 'warning', title: 'Warning', dataIndex: 'warning' },
            { code: 'output', title: 'Output', dataIndex: 'output' },
        ];

        return (
            <Modal
                isVisible={modalVisible}
                isClosable={true}
                onCancel={handleCancel}
                width={1000}
                extraProps={{ footer: null }}
            >
                <div className={devicesCss['log-content']}>
                    <BasicTable
                        className={devicesCss.deploymentData}
                        columns={columns}
                        dataSource={modalSource?.deploymentLogDataRecords || []}
                        rowKey='name'
                    />
                </div>
            </Modal>
        );
    };

    return (
        <div className={devicesCss.tabPane}>
            {memoizedStore.error != undefined && (
                <DangerAlert closable={false} message='Error' description={memoizedStore.error} />
            )}
            <AdvanceTable
                title=''
                table={{
                    dataSource: memoizedStore.displayDeployments,
                    columns: columns,
                }}
                loadingState={loading}
                toolbar={{
                    onReload: () => memoizedStore.fetchLatestData(),
                    extraItems: [
                        <ConstrainedDateRangePicker
                            key='deployments-date-range-picker'
                            range={DEFAULT_HISTORICAL_RANGE}
                            setFilterByDateFrom={(filterByDateFrom: Date) =>
                                memoizedStore.setFilterByDateFrom(filterByDateFrom)
                            }
                            setFilterByDateTo={(filterByDateTo: Date) =>
                                memoizedStore.setFilterByDateTo(filterByDateTo)
                            }
                            filterByDateFrom={memoizedStore.filterByDateFrom}
                            filterByDateTo={memoizedStore.filterByDateTo}
                            loading={loading}
                            onLoad={() => memoizedStore.fetchLatestData(true)}
                        />,
                    ],
                }}
            />
            <LogModal />
        </div>
    );
});
